import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { DashboardError, DashboardPage } from '../../components/JobsAccount';
import {
  IResumeEducation,
  IResumeLookup,
  IResumeWorkHistory,
  IUpdateMyResume,
  ResumeVisibility,
  useDeleteResumeEducationMutation,
  useDeleteResumeWorkHistoryMutation,
  useGetMyResumeQuery,
  useGetResumeExpertisesQuery,
  useGetResumeIndustrySectorsQuery,
  useSaveResumeEducationMutation,
  useSaveResumeWorkHistoryMutation,
  useUpdateMyResumeMutation,
  useUpdateResumeVisibilityMutation,
  useUploadResumeFileMutation,
} from '../../services/serviceResume';
import { gtmPush } from '../../utils/gtm';
import { hsIdentify, hsPush } from '../../utils/hubspot';
import { EmployerContactHistory } from './contactHistory';

const emptyResume: IUpdateMyResume = {
  headline: '',
  primaryLocation: '',
  overview: '',
  specialSkills: '',
  expertiseOther: '',
  industrySectorOther: '',
  willingToRelocate: false,
  willingToRelocateExplanation: '',
  expertiseIds: [],
  industrySectorIds: [],
};

const emptyEducation: IResumeEducation = {
  schoolId: 0,
  name: '',
  degree: '',
  city: '',
  state: '',
  graduation: null,
  description: '',
};

const emptyWork: IResumeWorkHistory = {
  previousEmployerId: 0,
  employerName: '',
  jobTitle: '',
  city: '',
  state: '',
  country: '',
  startDate: null,
  endDate: null,
  description: '',
};

const Section = ({
  title,
  children,
  id,
}: {
  title: string;
  children: React.ReactNode;
  id?: string;
}) => (
  <Paper id={id} variant="outlined" sx={{ borderRadius: 0, p: { xs: 2, md: 3 }, scrollMarginTop: 24 }}>
    <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{title}</Typography>
    {children}
  </Paper>
);

const toInputDate = (value: string | null) => (value ? value.slice(0, 10) : '');
const visibilityNames = ['private', 'searchable', 'public'] as const;

export const MyResume = () => {
  const resume = useGetMyResumeQuery();
  const expertises = useGetResumeExpertisesQuery();
  const sectors = useGetResumeIndustrySectorsQuery();
  const [updateResume, updateResult] = useUpdateMyResumeMutation();
  const [updateVisibility, visibilityResult] = useUpdateResumeVisibilityMutation();
  const [uploadFile, uploadResult] = useUploadResumeFileMutation();
  const [saveEducation, educationResult] = useSaveResumeEducationMutation();
  const [deleteEducation] = useDeleteResumeEducationMutation();
  const [saveWork, workResult] = useSaveResumeWorkHistoryMutation();
  const [deleteWork] = useDeleteResumeWorkHistoryMutation();
  const [form, setForm] = useState<IUpdateMyResume>(emptyResume);
  const [education, setEducation] = useState<IResumeEducation>(emptyEducation);
  const [work, setWork] = useState<IResumeWorkHistory>(emptyWork);
  const [dirty, setDirty] = useState(false);
  const hydratedResumeGuid = useRef<string | null>(null);
  const visibilityIsWorking = visibilityResult.isLoading || updateResult.isLoading;

  useEffect(() => {
    if (!resume.data || hydratedResumeGuid.current === resume.data.rguid) return;
    setForm({
      headline: resume.data.headline || '',
      primaryLocation: resume.data.primaryLocation || '',
      overview: resume.data.overview || '',
      specialSkills: resume.data.specialSkills || '',
      expertiseOther: resume.data.expertiseOther || '',
      industrySectorOther: resume.data.industrySectorOther || '',
      willingToRelocate: resume.data.willingToRelocate,
      willingToRelocateExplanation: resume.data.willingToRelocateExplanation || '',
      expertiseIds: resume.data.expertiseIds || [],
      industrySectorIds: resume.data.industrySectorIds || [],
    });
    hydratedResumeGuid.current = resume.data.rguid;
    setDirty(false);
  }, [resume.data]);

  const updateField = <K extends keyof IUpdateMyResume>(field: K, value: IUpdateMyResume[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setDirty(true);
  };

  const saveMain = async (): Promise<boolean> => {
    try {
      await updateResume(form).unwrap();
      setDirty(false);
      return true;
    } catch {
      // Mutation error is rendered below.
      return false;
    }
  };

  const changeVisibility = async (visibility: ResumeVisibility) => {
    if (visibilityIsWorking) return;

    // Capture the current query value before either mutation can invalidate and
    // refetch MyResume. This render's handler retains the pre-mutation value.
    const previousVisibility = resume.data?.visibility;
    if (previousVisibility === undefined || previousVisibility === visibility) return;

    // Searchable and Public expose the saved profile. Persist any edits first so
    // the content on screen is the content employers can actually discover.
    if (visibility !== 0 && dirty && !(await saveMain())) return;

    try {
      await updateVisibility(visibility).unwrap();
      gtmPush({
        event: 'jobs_talent_network_visibility_changed',
        previous_visibility: visibilityNames[previousVisibility],
        visibility: visibilityNames[visibility],
      });
      // Opting INTO the talent network (visibility !== 0) is an email-capture
      // moment: tie the seeker to their HubSpot contact and flush (identify
      // only sends with the next tracked event).
      if (visibility !== 0 && resume.data?.email) {
        hsIdentify(resume.data.email);
        hsPush('trackPageView');
      }
    } catch {
      // Mutation error is rendered below.
    }
  };

  const saveEducationRow = async () => {
    if (!education.name.trim()) return;
    try {
      await saveEducation(education).unwrap();
      setEducation(emptyEducation);
    } catch {
      // Keep values so the seeker can retry.
    }
  };

  const saveWorkRow = async () => {
    if (!work.employerName.trim()) return;
    try {
      await saveWork(work).unwrap();
      setWork(emptyWork);
    } catch {
      // Keep values so the seeker can retry.
    }
  };

  const visibilityOptions: Array<{
    value: ResumeVisibility;
    title: string;
    description: string;
  }> = [
    {
      value: 0,
      title: 'Private',
      description: 'Only you can see this resume. It does not appear in employer searches, and '
        + 'employers who previously had access lose it. Details an employer already received or '
        + 'copied cannot be taken back.',
    },
    {
      value: 1,
      title: 'Searchable: your name stays hidden',
      description: 'Eligible employers can find your experience under an anonymous alias. Your name, '
        + 'email, phone, address, and resume file stay hidden until you approve an introduction.',
    },
    {
      value: 2,
      title: 'Public: your name is shown',
      description: 'Same as Searchable, except eligible employers see your real name. Your email, '
        + 'phone, address, and resume file still stay hidden until you approve an introduction.',
    },
  ];

  return (
    <DashboardPage title="My Resume" isLoading={resume.isLoading}>
      <DashboardError error={resume.error} fallback="We could not load your resume." />
      <DashboardError
        error={updateResult.error || visibilityResult.error || uploadResult.error || educationResult.error || workResult.error}
        fallback="We could not save that resume change. Please try again."
      />
      {!dirty && (updateResult.isSuccess || uploadResult.isSuccess || educationResult.isSuccess || workResult.isSuccess) ? (
        <Alert severity="success" sx={{ mb: 2 }}>Your resume has been saved.</Alert>
      ) : null}
      {visibilityResult.isSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {visibilityResult.data?.visibility === 0
            ? 'Your resume is private and no longer appears in employer searches.'
            : visibilityResult.data?.visibility === 1
              ? 'Your resume is now searchable. Your name, contact details, and file stay hidden until you approve an introduction.'
              : 'Your resume is now public. Eligible employers see your name. Your contact details and file still stay hidden until you approve an introduction.'}
        </Alert>
      ) : null}

      {resume.data ? (
        <Stack spacing={3} sx={{ pb: 10 }}>
          <Paper
            variant="outlined"
            sx={{
              backgroundColor: resume.data.visibility === 0 ? 'var(--accent-soft)' : 'var(--go-soft)',
              borderColor: resume.data.visibility === 0 ? 'var(--accent)' : 'var(--go)',
              borderRadius: 0,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Typography sx={{ color: 'var(--accent-text)', fontSize: '0.8rem', fontWeight: 700 }}>
              CPG Talent Network
            </Typography>
            <Typography component="h2" variant="h5" sx={{ mt: 0.8 }}>
              {resume.data.visibility === 0
                ? 'Build your profile, then choose how employers find you'
                : resume.data.visibility === 1
                  ? 'Your masked resume is searchable'
                  : 'Your public resume is discoverable to eligible employers'}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 800 }}>
              {resume.data.visibility === 0
                ? 'Complete the sections below and choose Searchable or Public to join the Talent Network. Nothing is shared while your resume is Private.'
                : resume.data.visibility === 1
                  ? 'Eligible employers can discover your experience, but your name, contact details, and file stay hidden until you approve an introduction.'
                  : 'Eligible employers can discover your experience and see your name. Your contact details and file stay hidden until you approve an introduction.'}
            </Typography>
            {/* Two CTAs plus the step hint need more room than `sm` leaves them: without
                alignItems the buttons stretch to the hint's height, and without the wider
                breakpoint they shrink until their labels wrap onto three lines. */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1.5}
              sx={{ mt: 2.5 }}
            >
              {resume.data.visibility === 0 ? (
                <>
                  <Button
                    variant="contained"
                    disabled={visibilityIsWorking}
                    onClick={() => changeVisibility(1)}
                  >
                    {dirty ? 'Save changes & make searchable' : 'Make my resume searchable'}
                  </Button>
                  <Button component="a" href="#resume-visibility" variant="outlined">Compare visibility options</Button>
                </>
              ) : (
                <Button component="a" href="#resume-visibility" variant="contained">Review visibility</Button>
              )}
              <Typography color="text.secondary" variant="body2" sx={{ alignSelf: { sm: 'center' } }}>
                1. Build your profile&nbsp;&nbsp; 2. Choose visibility&nbsp;&nbsp; 3. Keep it current
              </Typography>
            </Stack>
          </Paper>
          <Paper
            variant="outlined"
            sx={{ borderRadius: 0, boxShadow: '6px 6px 0 var(--accent-soft)', p: { xs: 2, md: 3 } }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={3}>
              <Box>
                <Typography component="h2" variant="h6">Resume completeness</Typography>
                <Typography color="text.secondary">Complete each section to help employers understand your experience.</Typography>
              </Box>
              <Typography variant="h4" sx={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>
                {resume.data.completeness}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={resume.data.completeness}
              sx={{ height: 10, mt: 2 }}
            />
          </Paper>

          <Section title="Contact & headline">
            <Alert icon={<LockOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
              Your name, email, and phone come from your BevNET CPG Media account.
            </Alert>
            <Stack spacing={2}>
              <TextField value={`${resume.data.firstName || ''} ${resume.data.lastName || ''}`.trim()} label="Name" disabled />
              <TextField value={resume.data.email || ''} label="Email" disabled />
              <TextField value={resume.data.phone || ''} label="Phone" disabled />
              <TextField value={form.headline} onChange={(event) => updateField('headline', event.target.value)} label="Professional headline" inputProps={{ maxLength: 200 }} />
              <TextField value={form.primaryLocation} onChange={(event) => updateField('primaryLocation', event.target.value)} label="Primary location" inputProps={{ maxLength: 200 }} />
            </Stack>
          </Section>

          <Section title="Overview">
            <TextField
              value={form.overview}
              onChange={(event) => updateField('overview', event.target.value)}
              label="Professional overview"
              multiline
              minRows={5}
              fullWidth
            />
          </Section>

          <Section title="Skills & industry sectors">
            <Stack spacing={2}>
              <Autocomplete<IResumeLookup, true, false, false>
                multiple
                options={expertises.data || []}
                getOptionLabel={(option) => option.name}
                value={(expertises.data || []).filter((item) => form.expertiseIds.indexOf(item.id) >= 0)}
                onChange={(_event, values) => updateField('expertiseIds', values.map((item) => item.id))}
                renderInput={(params) => <TextField {...params} label="Expertise" />}
              />
              <Autocomplete<IResumeLookup, true, false, false>
                multiple
                options={sectors.data || []}
                getOptionLabel={(option) => option.name}
                value={(sectors.data || []).filter((item) => form.industrySectorIds.indexOf(item.id) >= 0)}
                onChange={(_event, values) => updateField('industrySectorIds', values.map((item) => item.id))}
                renderInput={(params) => <TextField {...params} label="Industry sectors" />}
              />
              <TextField value={form.specialSkills} onChange={(event) => updateField('specialSkills', event.target.value)} label="Other skills" multiline minRows={2} />
              <FormControlLabel
                control={<Radio checked={form.willingToRelocate} onClick={() => updateField('willingToRelocate', !form.willingToRelocate)} />}
                label="Willing to relocate"
              />
              {form.willingToRelocate ? (
                <TextField value={form.willingToRelocateExplanation} onChange={(event) => updateField('willingToRelocateExplanation', event.target.value)} label="Relocation preferences" />
              ) : null}
            </Stack>
          </Section>

          <Section title="Work history">
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {resume.data.workHistory.map((row) => (
                <Stack key={row.previousEmployerId} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography fontWeight={800}>{row.jobTitle || 'Role'} · {row.employerName}</Typography>
                    <Typography color="text.secondary" variant="body2">{row.city} {row.state}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => setWork(row)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => deleteWork(row.previousEmployerId)}>Remove</Button>
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <Stack spacing={2} sx={{ backgroundColor: 'var(--bg-2)', mt: 3, p: 2 }}>
              <TextField value={work.employerName} onChange={(event) => setWork({ ...work, employerName: event.target.value })} label="Employer" required />
              <TextField value={work.jobTitle || ''} onChange={(event) => setWork({ ...work, jobTitle: event.target.value })} label="Job title" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField value={work.city || ''} onChange={(event) => setWork({ ...work, city: event.target.value })} label="City" fullWidth />
                <TextField value={work.state || ''} onChange={(event) => setWork({ ...work, state: event.target.value })} label="State" fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField type="date" value={toInputDate(work.startDate)} onChange={(event) => setWork({ ...work, startDate: event.target.value || null })} label="Start date" InputLabelProps={{ shrink: true }} fullWidth />
                <TextField type="date" value={toInputDate(work.endDate)} onChange={(event) => setWork({ ...work, endDate: event.target.value || null })} label="End date" InputLabelProps={{ shrink: true }} fullWidth />
              </Stack>
              <TextField value={work.description || ''} onChange={(event) => setWork({ ...work, description: event.target.value })} label="Description" multiline minRows={3} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={!work.employerName.trim() || workResult.isLoading} onClick={saveWorkRow}>{work.previousEmployerId ? 'Update role' : 'Add role'}</Button>
                {work.previousEmployerId ? <Button onClick={() => setWork(emptyWork)}>Cancel</Button> : null}
              </Stack>
            </Stack>
          </Section>

          <Section title="Education">
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {resume.data.education.map((row) => (
                <Stack key={row.schoolId} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography fontWeight={800}>{row.name}</Typography>
                    <Typography color="text.secondary" variant="body2">{row.degree || 'Degree not listed'}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => setEducation(row)}>Edit</Button>
                    <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => deleteEducation(row.schoolId)}>Remove</Button>
                  </Stack>
                </Stack>
              ))}
            </Stack>
            <Stack spacing={2} sx={{ backgroundColor: 'var(--bg-2)', mt: 3, p: 2 }}>
              <TextField value={education.name} onChange={(event) => setEducation({ ...education, name: event.target.value })} label="School" required />
              <TextField value={education.degree || ''} onChange={(event) => setEducation({ ...education, degree: event.target.value })} label="Degree" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField value={education.city || ''} onChange={(event) => setEducation({ ...education, city: event.target.value })} label="City" fullWidth />
                <TextField value={education.state || ''} onChange={(event) => setEducation({ ...education, state: event.target.value })} label="State" fullWidth />
              </Stack>
              <TextField type="date" value={toInputDate(education.graduation)} onChange={(event) => setEducation({ ...education, graduation: event.target.value || null })} label="Graduation" InputLabelProps={{ shrink: true }} />
              <TextField value={education.description || ''} onChange={(event) => setEducation({ ...education, description: event.target.value })} label="Description" multiline minRows={2} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={!education.name.trim() || educationResult.isLoading} onClick={saveEducationRow}>{education.schoolId ? 'Update education' : 'Add education'}</Button>
                {education.schoolId ? <Button onClick={() => setEducation(emptyEducation)}>Cancel</Button> : null}
              </Stack>
            </Stack>
          </Section>

          <Section title="Resume file">
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
              {resume.data.file.hasFile ? (
                <Chip label={resume.data.file.fileName || 'Uploaded resume'} variant="outlined" />
              ) : <Typography color="text.secondary">No resume file uploaded.</Typography>}
              {resume.data.file.downloadUrl ? <Button href={resume.data.file.downloadUrl} target="_blank">Download current file</Button> : null}
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={uploadResult.isLoading}>
                {resume.data.file.hasFile ? 'Replace file' : 'Upload file'}
                <input
                  hidden
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0];
                    if (file) uploadFile(file);
                  }}
                />
              </Button>
            </Stack>
            <Typography color="text.secondary" variant="caption">PDF or Word, up to 10 MB.</Typography>
          </Section>

          <Section title="Employer contact history" id="employer-contact-history">
            <EmployerContactHistory />
          </Section>

          <Section title="Join the CPG Talent Network" id="resume-visibility">
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Choose Private to keep working on your profile, Searchable to appear anonymously, or
              Public to appear under your real name. In every non-private mode your contact details
              and resume file stay hidden until you approve an introduction.
            </Typography>
            {resume.data.visibility !== 0 ? (
              <Alert icon={<LockOutlinedIcon />} severity="warning" sx={{ mb: 2 }}>
                Anything you type into your headline, overview, skills, work history, or education is
                visible to eligible employers. Leave your email, phone number, and anything else you
                want to keep private out of those fields unless you are happy for employers to see it.
              </Alert>
            ) : null}
            {dirty ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                We’ll save your profile changes before making your resume Searchable or Public.
              </Alert>
            ) : null}
            <Stack spacing={1.5}>
              {visibilityOptions.map((option) => {
                const selected = resume.data?.visibility === option.value;
                return (
                  <Paper
                    key={option.value}
                    component="label"
                    variant="outlined"
                    sx={{
                      backgroundColor: selected ? 'var(--accent-soft)' : 'white',
                      borderColor: selected ? 'var(--accent)' : 'var(--line)',
                      borderRadius: 0,
                      cursor: visibilityIsWorking ? 'wait' : 'pointer',
                      p: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Radio
                        checked={selected}
                        disabled={visibilityIsWorking}
                        onChange={() => changeVisibility(option.value)}
                      />
                      <Box>
                        <Typography fontWeight={800}>{option.title}</Typography>
                        <Typography color="text.secondary">{option.description}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Section>
        </Stack>
      ) : null}

      {resume.data ? (
        <Paper
          square
          elevation={4}
          sx={{ bottom: 0, left: 0, position: 'fixed', right: 0, zIndex: 10 }}
        >
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ margin: '0 auto', maxWidth: 1200, px: 3, py: 1.5 }}>
            <Typography color="text.secondary">{dirty ? 'Unsaved profile changes' : 'Profile details saved'}</Typography>
            <Button variant="contained" disabled={!dirty || updateResult.isLoading} onClick={saveMain}>Save resume</Button>
          </Stack>
        </Paper>
      ) : null}
    </DashboardPage>
  );
};

export default MyResume;
